import React, { useState, useEffect, useRef, useMemo, memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import classname from 'classnames';

import { GOOGLE, MAP, MapContext } from './constants';

export const _initializeMap = ({
	google, setMap, center, zoom, mapControls, [MAP]: map, handleIdle, initialBounds, handleZoom, onZoomChanged,
	setPrevZoom, onIdle, setDidDrag, gestureHandling = 'greedy', preselectedFacilityId
}, node) => {
	if (node && google && !map) {
		// Set the map style for 2 reasons:
		// 1. We don't want markers for the default POIs on the map
		// 2. Setting the style this way changes to Google logo on the map to all white, which looks better.
		const styledMapType = new google.maps.StyledMapType([
			{
				featureType: 'landscape',
				elementType: 'labels',
				stylers: [
					{ visibility: 'off' }
				]
			},
			{
				featureType: 'poi',
				elementType: 'labels',
				stylers: [
					{ visibility: 'off' }
				]
			}
		]);

		const newMap = new google.maps.Map(node, {
			center,
			zoom,
			clickableIcons: false,
			gestureHandling,
			mapTypeControlOptions: {
				mapTypeIds: ['roadmap', 'styled_map']
			},
			...mapControls
		});

		const l = newMap.addListener('idle', () => {
			if (initialBounds && !preselectedFacilityId) {
				newMap.fitBounds(initialBounds);
				const newZoom = newMap.getZoom();
				setPrevZoom(newZoom);
				onZoomChanged(newZoom);
			}

			google.maps.event.removeListener(l);
			onIdle(newMap);
			newMap.addListener('idle', () => handleIdle(newMap));
			newMap.addListener('zoom_changed', () => handleZoom(newMap.getZoom()));
			newMap.addListener('dragend', () => setDidDrag(true));
		});

		newMap.mapTypes.set('styled_map', styledMapType);
		newMap.setMapTypeId('styled_map');
		setMap(newMap);
	}
};

const GoogleMap = memo(function GoogleMap({
	onZoomChanged = noop,
	onIdle = noop,
	onDragEnd = noop,
	onBoundsChange = noop,
	onRef = noop,
	dragThreshold = 1000,
	mapControls = {
		zoomControl: false,
		mapTypeControl: false,
		scaleControl: false,
		streetViewControl: false,
		rotateControl: false,
		fullscreenControl: false
	},
	google,
	center,
	zoom,
	initialBounds,
	preselectedFacilityId,
	gestureHandling,
	className,
	children,
}) {
	const [map, setMap] = useState(null);
	const [bounds, setBounds] = useState(null);
	const [idleEventDisabled, setDisableIdleEvent] = useState(null);
	const [prevZoom, setPrevZoom] = useState(null);
	const [didDrag, setDidDrag] = useState(false);
	const mapRef = useRef(null);
	const prevZoomRef = useRef(zoom);

	// Use refs to avoid stale closures in google map event listeners
	const stateRef = useRef({});
	stateRef.current = {
		bounds, didDrag, idleEventDisabled, map,
		onZoomChanged, onBoundsChange, onIdle, center, dragThreshold,
		google, setPrevZoom, setBounds, setDidDrag,
	};

	const handleZoom = useCallback((newZoom) => {
		const { onZoomChanged, onBoundsChange, setPrevZoom, map } = stateRef.current;
		onZoomChanged(newZoom);
		if (map) {
			onBoundsChange(map.getBounds());
		}
		setPrevZoom(newZoom);
	}, []);

	const handleIdle = useCallback((mapInstance) => {
		const {
			google, onBoundsChange, center, dragThreshold, onIdle, bounds,
			setBounds, idleEventDisabled, setDidDrag, didDrag
		} = stateRef.current;
		if (!idleEventDisabled) {
			onIdle();
			setDidDrag(false);
			const newBounds = mapInstance.getBounds();

			if (newBounds && didDrag) {
				// Determine how far the map was dragged
				const p1 = (bounds && bounds.getCenter()) || (new google.maps.LatLng(center));
				const p2 = newBounds.getCenter();
				const distance = google.maps.geometry.spherical.computeDistanceBetween(p1, p2);
				if (distance > dragThreshold) {
					onBoundsChange(newBounds);
					setBounds(newBounds);
				}
			}
		}
	}, []);

	// Initialize map on mount
	useEffect(() => {
		_initializeMap({
			google, setMap, center, zoom, mapControls, [MAP]: map,
			handleIdle, initialBounds, handleZoom, onZoomChanged,
			setPrevZoom, onIdle, setDidDrag, gestureHandling, preselectedFacilityId
		}, mapRef.current);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Handle zoom prop changes
	useEffect(() => {
		if ((prevZoomRef.current !== zoom) && map) {
			map.setZoom(zoom);
		}
		prevZoomRef.current = zoom;
	}, [zoom, map]);

	const contextValue = useMemo(() => ({
		[MAP]: map,
		[GOOGLE]: google,
		disableIdleEvent: setDisableIdleEvent,
	}), [map, google]);

	return (
		<MapContext.Provider value={contextValue}>
			<div className={classname('ph-google-map', className)}>
				<div className="gmap" ref={mapRef} />
				{children}
			</div>
		</MapContext.Provider>
	);
});

GoogleMap.propTypes = {
	className: PropTypes.string,
	children: PropTypes.node,
	zoom: PropTypes.number
};

export { GoogleMap };
export default GoogleMap;
